import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import ModalHeader from '../../../../../Modal/components/ModalHeader/ModalHeader';
import ScoreGradeBar from '../ScoreGradeBar/ScoreGradeBar';
import style from './style/singleScorePager.scss';
import { ToxicsIcon } from '../DerailmentIcon';
import { HtmlFromString } from '../../../../../HtmlFromString';
import { RiskBar } from './RiskBar';
import { TOXICS_RISKS } from '../../constants';

/**
 *
 * @param {Object} props - props object
 * @param {Object} props.i18nParticipation - i18n object
 * @param {Object} props.score - score object
 * @param {number} props.score.score - score value
 * @param {string} props.score.riskAssessment - risk assessment
 * @param {Function} props.onClose - close modal
 * @param {Function} props.goNext - go to next score
 * @param {Function} props.goBack - go to previous score
 * @param {boolean} props.canGoBack - true if can go to previous score
 * @param {boolean} props.canGoNext - true if can go to next score
 * @param {Function} props.openAllScoresModal - open modal to show all scores
 *
 * @returns {JSX.Element} - single score pager
 */
const SingleScorePager = props => {
    const {
        i18nParticipation,
        title,
        score,
        onClose,
        goNext,
        goBack,
        canGoBack,
        canGoNext,
        openAllScoresModal
    } = props;

    const scaleDefinition =
    i18nParticipation[`report_derailment_scale_${score.handle}_definition`];

    const riskLabel = i18nParticipation[TOXICS_RISKS[score.score - 1]];

    return (
    <>
      <ModalHeader classes={{ modalHeader: style.modalHeader }}>
          <header>
              <div>
                  <ToxicsIcon scaleHandle={score.handle} score={score.score} />
              </div>
              <section>
                  <span>{title}</span>
                  <h2>{score.name}</h2>
              </section>
          </header>
          <button type="button" onClick={onClose}>
              <FontAwesomeIcon icon={'times'} />
          </button>
      </ModalHeader>

      <RiskBar riskAssessment={score.riskAssessment} label={riskLabel} />

      <div className={style.container}>
          <div className={style.content}>
              <div className={style.score_container}>
                  <p className={style.scaleDescription}>{scaleDefinition}</p>

                  <ScoreGradeBar
                      i18nParticipation={i18nParticipation}
                      score={score.score}
                      hasLabels
                  />

                  <ScoreSubscales
                      label={
                          i18nParticipation.report_derailment_single_score_explanation
                      }
                      texts={score.subscales}
                  />
              </div>

              <PositiveAndNegativeScoreDefinitions
                  i18nParticipation={i18nParticipation}
                  scoreHandle={score.handle}
              />
          </div>

          <ScorePageControls
              i18nParticipation={i18nParticipation}
              goNext={goNext}
              goBack={goBack}
              canGoBack={canGoBack}
              canGoNext={canGoNext}
              openAllScoresModal={openAllScoresModal}
          />
      </div>
    </>
    );
};

export default SingleScorePager;

/**
 *
 * @param {Object} props - props object
 * @param {string} props.label - label
 * @param {Array<string>} props.texts - texts
 *
 * @returns {JSX.Element} - score subscales
 */
const ScoreSubscales = ({ label, texts }) => (
    <div className={style.subscalesContainer}>
        <p>{label}</p>

        <ul>
            {texts?.map?.((subscale, idx) => {
                const text = subscale?.text;

                return <li key={idx}>{text}</li>;
            })}
        </ul>
    </div>
);

const ScorePageControls = props => {
    const {
        i18nParticipation,
        goNext,
        goBack,
        canGoBack,
        canGoNext,
        openAllScoresModal
    } = props;

    return (
        <div className={style.scorePageControlsContainer}>
            <div>
                { canGoBack ? (
                    <button
                        className="action_button modal"
                        onClick={goBack}
                    >
                        <FontAwesomeIcon icon={'arrow-left'} />
                        {i18nParticipation.report_derailment_back}
                    </button>
                ) : null}
            </div>

            <div
                className={style.scorePageControlsToOverview}
                onClick={openAllScoresModal}
                role='button'
                tabIndex={0}
            >
                {i18nParticipation.report_derailment_back_to_overview}
            </div>

            <div>
                {canGoNext ? (
                    <button
                        className="action_button modal"
                        onClick={goNext}
                    >
                        {i18nParticipation.report_derailment_next}
                        <FontAwesomeIcon icon={'arrow-right'} />
                    </button>
                ) : null}
            </div>
        </div>
    );
};

/**
 *
 * @param {Object} props - props object
 * @param {Object} props.i18nParticipation - i18n object
 * @param {string} props.scoreHandle - score handle
 *
 * @returns {JSX.Element} - positive and negative score definitions
 */
function PositiveAndNegativeScoreDefinitions(props) {
    const { i18nParticipation, scoreHandle } = props;

    return (
        <div className={style.scoreExplanation_container}>
            <div className={style.header}>
                <FontAwesomeIcon className={style.square} icon={'square'} />
                <span className={style.label}>{i18nParticipation.report_positive}</span>
            </div>

            <HtmlFromString
                tag={'p'}
                className={style.paragraph}
                content={i18nParticipation[`report_derailment_scale_${scoreHandle}_positive_definitions`]}
            />

            <br className={style.break} />

            <div className={style.header}>
                <FontAwesomeIcon className={style.square} icon={'square'} />
                <span className={style.label}>{i18nParticipation.report_negative}</span>
            </div>

            <HtmlFromString
                tag={'p'}
                className={style.paragraph}
                content={i18nParticipation[`report_derailment_scale_${scoreHandle}_negative_definitions`]}
            />
        </div>
    );
}
